import { css } from '@emotion/react';
import { Alert, Button, Chip, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { getRouteTitle, ROUTES } from '../../../constants/RouteNameMapping';
import {
  DocsRole,
  LedgerRole,
  Precedence,
  RoundAssignmentMode,
  RoundCreationSettingsResponse,
  RoundMatchMode,
} from '../../../data-models/round-creation-settings.data-model';
import { CompanySearchResponseWithRealId } from '../../../schemas/CompanySearchResponse.schema';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { getErrorMessage } from '../../../services/queryHelpers';
import { FMT } from '../../../util/formatter-service';
import { getRoundLabel } from '../../../util/preference-utils';
import { CompanySearch } from '../../../components/CompanySearch/CompanySearch';
import { ConfigStateChip } from '../components/ConfigStateChip';
import { ExitConfigBlocker } from '../DataDisplay/ExitConfigBlocker';
import { RebuildRoundsDialog } from './RebuildRoundsDialog';
import {
  RoundCreationFormValues,
  toSettingsUpdate,
  windowIsRequired,
  WINDOW_MAX_DAYS,
} from './roundCreationConfig.schema';
import {
  assignmentModeCopy,
  ASSIGNMENT_MODE_ORDER,
  condNotes,
  CONFIG_KEYS,
  deltaLabel,
  creationModeCopy,
  docsRoleCopy,
  ledgerRoleCopy,
  MATCH_MODE_ORDER,
  precedenceCopy,
  sectionCopy,
  securityAssignmentModeCopy,
  securityTypeLabel,
  settingTips,
  sourcesCopy,
} from './roundCreationCopy';
import { RoundCreationPreviewDialog, toSearchOption } from './RoundCreationPreview';
import { pillCss, pillWarnCss } from './roundCreationStyles';
import {
  ChoiceSetting,
  CondNote,
  InfoBox,
  InheritedNote,
  KeyChip,
  NumberSetting,
  SecurityTypesSetting,
  SettingsSection,
  SwitchSetting,
} from './RoundCreationSettingControls';
import { useRoundCreationPreview } from './useRoundCreationPreview';
import { useRoundCreationConfigFormState } from './useRoundCreationConfigFormState';
import { useRoundSecurityTypes } from './useRoundCreationConfigActions';

// The prototype's reading column: wide enough for the help sentences, narrow enough to scan.
const pageCss = css`
  max-width: 780px;
`;

const pageSubtitleCss = css`
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--mui-colors-text-secondary);
  max-width: 560px;
`;

const noteCss = css`
  font-size: 11px;
  line-height: 1.5;
  color: var(--mui-colors-text-secondary);
`;

/** The prototype's impact strip: the one number the whole page exists to move. */
/* A grid rather than a wrapping row: the reading half may grow or shrink, and the controls keep
   their place instead of jumping to a line of their own whenever the sentence changes length. */
const impactStripCss = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--mui-colors-border-card);
  border-radius: 10px;
  background: var(--mui-colors-neutral-5);
  padding: 10px 14px;
  margin-bottom: 16px;
`;

const impactReadingCss = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

/* Pushed right so the picker and its button read as one control, whatever the sentence's length. */
const impactPickerCss = css`
  width: 15rem;
`;

const impactCountCss = css`
  font-size: 22px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

// Sticky so Save is reachable from any card without scrolling back up, as in the prototype.
const footerCss = css`
  position: sticky;
  bottom: 0;
  z-index: 1;
  /* Pushed to the bottom edge even when the settings are short enough not to scroll. */
  margin-top: auto;
  width: 100%;
  /* Without this the padding is added to the width and the page scrolls sideways. */
  box-sizing: border-box;
  padding: 12px;
  background: var(--mui-colors-primary-0);
  border-top: 1px solid var(--mui-colors-border-card);
  /* Without a shadow the bar reads as a card edge, and the setting it covers looks half-drawn. */
  box-shadow: 0 -4px 12px rgba(16, 37, 62, 0.08);
`;

/* Save and Cancel line up with the left edge of the cards; Reset sits at the far right of the bar,
   where a destructive-ish action belongs and where the eye is not looking while saving. */
const footerInnerCss = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
`;

const LEDGER_ROLE_ORDER = [LedgerRole.CREATE, LedgerRole.TAG, LedgerRole.IGNORE];
const DOCS_ROLE_ORDER = [DocsRole.ENRICH_ONLY, DocsRole.CREATE];
const PRECEDENCE_ORDER = [Precedence.LEDGER_LEADS, Precedence.DOCS_LEAD];

export function RoundCreationConfig() {
  const {
    editStatus,
    methods,
    onSave,
    onReset,
    onCancel,
    settings,
    isLoading,
    loadError,
    rebuild,
    isRebuilding,
    onApplied,
    draft,
  } = useRoundCreationConfigFormState();
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // One company for the page, held as the picker's own option: the strip's headline and the modal's
  // table must never disagree, and the modal's picker must not open empty over a populated table.
  const [company, setCompany] = useState<CompanySearchResponseWithRealId | null>(null);
  const roundLabel = getRoundLabel();
  const copy = sectionCopy(roundLabel);
  const tips = settingTips(roundLabel);
  const canRebuild = PermissionService.get().hasPermission(PermissionKey.customerSuccessAll);

  if (isLoading) {
    return (
      <Stack width='100%' height='100%' alignItems='center' justifyContent='center'>
        <CircularProgress />
      </Stack>
    );
  }
  if (loadError || !settings) {
    return (
      <Stack padding='1rem'>
        <Alert severity='error'>{getErrorMessage(loadError, 'The settings could not be loaded')}</Alert>
      </Stack>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack width='100%' height='100%' overflow='auto'>
        <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
        <Stack width='100%' css={pageCss} padding='8px 12px 0'>
          <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap='1rem' mb='1rem'>
            <Stack>
              <Stack direction='row' alignItems='center' columnGap='0.5rem' mb='0.25rem'>
                <Typography variant='h1' sx={{ fontSize: '22px' }}>
                  {getRouteTitle(ROUTES.ADMIN_PANEL_ROUND_CREATION)}
                </Typography>
                <ConfigStateChip configStatus={editStatus} />
              </Stack>
              <p css={pageSubtitleCss}>{copy.pageIntro}</p>
            </Stack>
            <Chip size='small' variant='outlined' label={copy.appliesTo} />
          </Stack>

          {!settings.seeded && (
            <Alert severity='warning' sx={{ mb: '0.75rem' }}>
              {copy.notSeeded}
            </Alert>
          )}
          <Alert severity='info' sx={{ mb: '0.75rem' }}>
            {copy.banner}
          </Alert>

          {canRebuild && (
            <Stack direction='row' alignItems='center' gap='0.75rem' mb='1rem' flexWrap='wrap'>
              <Button
                variant='outlined'
                color='secondary'
                size='small'
                onClick={() => setRebuildOpen(true)}
                disabled={!methods.formState.isValid}
              >
                {copy.reprocess.label}
              </Button>
              <Chip size='small' label={copy.reprocess.badge} />
              <span css={noteCss}>{copy.reprocess.help}</span>
            </Stack>
          )}

          <ImpactStrip
            draft={draft}
            company={company}
            onPickCompany={setCompany}
            onOpenPreview={() => setPreviewOpen(true)}
          />

          <Stack gap='1rem'>
            <SourcesSection effective={settings.effective} />
            <AutoCreationSection />
            <MatchRuleSection
              title={copy.matching.title}
              modeLabel={copy.matching.modeLabel}
              modeField='roundCreationMatchMode'
              modeKeyChip={CONFIG_KEYS.matchBy}
              modeTip={tips.matchBy}
              windowField='roundCreationWindowDays'
              windowLabel={copy.matching.windowLabel}
              windowHelp={copy.matching.windowHelp}
              windowKeyChip={CONFIG_KEYS.creationWindow}
              windowTip={tips.creationWindow}
              modes={creationModeCopy(roundLabel)}
              defaultWindow={settings.defaults.roundCreationWindowDays}
              windowUnusedNote={condNotes(roundLabel).windowUnusedUnderStage}
            />
            <MatchRuleSection
              title={copy.assignment.title}
              modeLabel={copy.assignment.modeLabel}
              modeField='transactionAssignmentMatchMode'
              modeKeyChip={CONFIG_KEYS.matchBy}
              modeTip={tips.matchBy}
              windowField='transactionAssignmentWindowDays'
              windowLabel={copy.assignment.windowLabel}
              windowHelp={copy.assignment.windowHelp}
              windowKeyChip={CONFIG_KEYS.assignmentWindow}
              windowTip={tips.assignmentWindow}
              modes={assignmentModeCopy(roundLabel)}
              defaultWindow={settings.defaults.transactionAssignmentWindowDays}
              windowUnusedNote={condNotes(roundLabel).windowUnusedUnderStage}
              beforeWindow={<AssignmentModeSetting title={copy.assignment.securitiesLabel} />}
              extra={
                <GraceSetting label={copy.grace.label} help={copy.grace.help} intro={copy.grace.intro} />
              }
            />
          </Stack>

          {settings.updatedAt && (
            <Typography variant='caption' color='text.secondary' mt='0.5rem' mb='1rem'>
              {`Last saved ${FMT.format('dateTimeShort', settings.updatedAt)}${settings.updatedBy ? ` by ${settings.updatedBy}` : ''} · version ${settings.settingsVersion}`}
            </Typography>
          )}
        </Stack>

        {/* Outside the reading column: the bar belongs to the page, so it spans it and sits on
            the bottom edge, while its buttons stay lined up with the cards above. */}
        <div css={footerCss}>
          <div css={footerInnerCss}>
            <Button
              variant='contained'
              color='secondary'
              onClick={() => setRebuildOpen(true)}
              disabled={editStatus !== 'modified' || !methods.formState.isValid}
            >
              {copy.footer.save}
            </Button>
            <Button
              variant='outlined'
              color='secondary'
              onClick={onCancel}
              disabled={editStatus !== 'modified'}
            >
              {copy.footer.cancel}
            </Button>
            <Button variant='text' color='secondary' onClick={onReset} sx={{ ml: 'auto' }}>
              {copy.footer.resetDefaults}
            </Button>
          </div>
        </div>

        <RoundCreationPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          draft={draft}
          company={company}
          onPickCompany={setCompany}
        />
        {/* Save routes through here: the reach of a settings change is never assumed. */}
        <RebuildRoundsDialog
          open={rebuildOpen}
          onClose={() => setRebuildOpen(false)}
          settings={toSettingsUpdate(methods.getValues(), settings.effective?.docsRole)}
          rebuild={rebuild}
          isRebuilding={isRebuilding}
          onApplied={onApplied}
          allowRetroactive={canRebuild}
        />
      </Stack>
    </FormProvider>
  );
}

/**
 * The count the settings produce for one company, above the controls that change it. Runs the same
 * preview the modal does, so opening the modal costs nothing and the two can never disagree.
 */
function ImpactStrip({
  draft,
  company,
  onPickCompany,
  onOpenPreview,
}: {
  draft: ReturnType<typeof useRoundCreationConfigFormState>['draft'];
  company: CompanySearchResponseWithRealId | null;
  onPickCompany: (company: CompanySearchResponseWithRealId | null) => void;
  onOpenPreview: () => void;
}) {
  const copy = sectionCopy(getRoundLabel()).impact;
  const { data, isFetching } = useRoundCreationPreview(company?.id ?? null, draft);
  // Previous results are kept on screen while the next load runs, so a count is only shown against
  // the company it was asked about — the response echoes the id, so the check costs nothing.
  const result = company && data?.companyId === company.id ? data : undefined;
  const planned = result?.plannedRoundCount ?? null;
  const today = result?.currentRoundCount ?? null;
  const delta = planned !== null && today !== null ? planned - today : null;

  return (
    <div css={impactStripCss}>
      <div css={impactReadingCss}>
        {planned !== null && <span css={impactCountCss}>{FMT.format('integer', planned)}</span>}
        <span css={noteCss}>{company ? copy.underConfig(company.fields.name) : copy.noCompany}</span>
        {isFetching && <CircularProgress size='0.9rem' />}
        {delta !== null && (
          <span css={[pillCss, delta !== 0 && pillWarnCss]}>
            {deltaLabel(planned as number, today as number, copy)}
          </span>
        )}
      </div>
      <div css={impactPickerCss}>
        {/* Keyed on the pick so a company chosen in the modal shows here too: the picker reads its
            initial value once, on mount. */}
        <CompanySearch
          key={company?.id ?? 'none'}
          fullWidth
          showPortcoOnly
          showSelectedOption
          showSelectedLogo
          initialValue={company ? toSearchOption(company) : undefined}
          placeholder={copy.pickCompany}
          onChange={onPickCompany}
          onClear={() => onPickCompany(null)}
        />
      </div>
      <Button variant='outlined' color='secondary' size='small' onClick={onOpenPreview}>
        {copy.preview}
      </Button>
    </div>
  );
}

/** Who is allowed to say a round happened. */
function SourcesSection({ effective }: { effective: RoundCreationSettingsResponse['effective'] }) {
  const { control, setValue } = useFormContext<RoundCreationFormValues>();
  const roundLabel = getRoundLabel();
  const copy = sourcesCopy(roundLabel).sources;
  const tips = settingTips(roundLabel);
  const ledgerRole = useWatch({ name: 'ledgerRole', control });
  const docsRole = useWatch({ name: 'docsRole', control });
  const precedence = useWatch({ name: 'precedence', control });

  return (
    <SettingsSection title={copy.title}>
      <ChoiceSetting
        title={copy.ledgerTitle}
        keyChip={CONFIG_KEYS.ledgerRole}
        tip={tips.ledgerRole}
        options={LEDGER_ROLE_ORDER}
        copy={ledgerRoleCopy(roundLabel)}
        // Null means the client's old configuration still decides; show what that resolves to. With
        // no resolved value to show, nothing is selected rather than a value nobody chose.
        value={ledgerRole ?? effective?.ledgerRole ?? null}
        onChange={(next) => setValue('ledgerRole', next, { shouldDirty: true, shouldValidate: true })}
        footnote={
          ledgerRole === null ? (
            <InheritedNote label={copy.inheritedLabel}>{copy.inheritedHelp}</InheritedNote>
          ) : undefined
        }
      />
      {effective?.legacyLedgerAmbiguous && <Alert severity='warning'>{copy.ambiguous}</Alert>}
      <ChoiceSetting
        title={copy.docsTitle}
        keyChip={CONFIG_KEYS.docsRole}
        tip={tips.docsRole}
        options={DOCS_ROLE_ORDER}
        copy={docsRoleCopy(roundLabel)}
        // Null means the client's existing roundProvider key still decides; show what that gives.
        value={docsRole ?? effective?.docsRole ?? null}
        onChange={(next) => setValue('docsRole', next, { shouldDirty: true, shouldValidate: true })}
        footnote={
          docsRole === null ? (
            <InheritedNote label={copy.inheritedLabel}>{copy.docsInheritedHelp}</InheritedNote>
          ) : undefined
        }
      />
      {/* Precedence only decides anything when both sources may create. */}
      <Collapse in={(docsRole ?? effective?.docsRole) === DocsRole.CREATE} unmountOnExit>
        <ChoiceSetting
          title={copy.precedenceTitle}
          help={copy.precedenceIntro}
          options={PRECEDENCE_ORDER}
          copy={precedenceCopy(roundLabel)}
          value={precedence ?? effective?.precedence ?? null}
          onChange={(next) => setValue('precedence', next, { shouldDirty: true, shouldValidate: true })}
        />
      </Collapse>
      <InfoBox>
        <b>{copy.consolidatedTitle}</b>{' '}
        {copy.consolidatedKeys.map((key) => (
          <KeyChip key={key} name={key} />
        ))}{' '}
        {copy.consolidatedHelp} {copy.relatedTitle}{' '}
        {copy.relatedKeys.map((key) => (
          <KeyChip key={key} name={key} />
        ))}
      </InfoBox>
    </SettingsSection>
  );
}

/** What counts as a round in the first place. */
function AutoCreationSection() {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<RoundCreationFormValues>();
  const roundLabel = getRoundLabel();
  const copy = sourcesCopy(roundLabel).autoCreation;
  const tips = settingTips(roundLabel);
  const conversions = useWatch({ name: 'conversionsCreateRounds', control });
  const securityTypes = useWatch({ name: 'securityTypes', control });
  const { data: options = [] } = useRoundSecurityTypes();

  return (
    <SettingsSection title={copy.title}>
      <SwitchSetting
        title={copy.conversionsLabel}
        keyChip={CONFIG_KEYS.conversions}
        tip={tips.conversions}
        help={copy.conversionsHelp}
        checked={conversions}
        onChange={(next) =>
          setValue('conversionsCreateRounds', next, { shouldDirty: true, shouldValidate: true })
        }
      />
      <SecurityTypesSetting
        title={copy.securitiesTitle}
        keyChip={CONFIG_KEYS.securityTypes}
        tip={tips.securityTypes}
        help={copy.securitiesHelp}
        selectAllLabel={copy.securitiesSelectAll}
        options={options}
        labelOf={securityTypeLabel}
        value={securityTypes}
        onChange={(next) =>
          setValue('securityTypes', next as RoundCreationFormValues['securityTypes'], {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
        error={errors.securityTypes?.message}
      />
    </SettingsSection>
  );
}

interface MatchRuleSectionProps {
  title: string;
  modeLabel: string;
  modeField: 'roundCreationMatchMode' | 'transactionAssignmentMatchMode';
  modeKeyChip: string;
  modeTip: string;
  windowField: 'roundCreationWindowDays' | 'transactionAssignmentWindowDays';
  windowLabel: string;
  windowHelp: string;
  windowKeyChip: string;
  windowTip: string;
  modes: Record<RoundMatchMode, { label: string; description: string }>;
  defaultWindow: number | null;
  windowUnusedNote: string;
  beforeWindow?: React.ReactNode;
  extra?: React.ReactNode;
}

function MatchRuleSection({
  title,
  modeLabel,
  modeField,
  modeKeyChip,
  modeTip,
  windowField,
  windowLabel,
  windowHelp,
  windowKeyChip,
  windowTip,
  modes,
  defaultWindow,
  windowUnusedNote,
  beforeWindow,
  extra,
}: MatchRuleSectionProps) {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<RoundCreationFormValues>();
  const mode = useWatch({ name: modeField, control });
  const window = useWatch({ name: windowField, control });

  return (
    <SettingsSection title={title}>
      <ChoiceSetting
        title={modeLabel}
        keyChip={modeKeyChip}
        tip={modeTip}
        options={MATCH_MODE_ORDER}
        copy={modes}
        value={mode}
        onChange={(next) => {
          setValue(modeField, next, { shouldDirty: true, shouldValidate: true });
          // Only fires while the field is still empty, which is the first time a window-bearing
          // mode is picked: start from the standard default rather than showing an error.
          if (windowIsRequired(next) && getValues(windowField) == null) {
            setValue(windowField, defaultWindow, { shouldDirty: true, shouldValidate: true });
          }
        }}
      />
      {beforeWindow}
      {!windowIsRequired(mode) && <CondNote>{windowUnusedNote}</CondNote>}
      <Collapse in={windowIsRequired(mode)} unmountOnExit>
        <NumberSetting
          title={windowLabel}
          keyChip={windowKeyChip}
          tip={windowTip}
          help={windowHelp}
          unit='days'
          value={window}
          onChange={(next) => setValue(windowField, next, { shouldDirty: true, shouldValidate: true })}
          error={errors[windowField]?.message}
          min={1}
          max={WINDOW_MAX_DAYS}
        />
      </Collapse>
      {extra}
    </SettingsSection>
  );
}

/** Which round wins when one window holds several securities. Sits with the attachment rules. */
function AssignmentModeSetting({ title }: { title: string }) {
  const { control, setValue } = useFormContext<RoundCreationFormValues>();
  const roundLabel = getRoundLabel();
  const assignmentMode = useWatch({ name: 'assignmentMode', control });

  return (
    <ChoiceSetting
      title={title}
      keyChip={CONFIG_KEYS.assignmentMode}
      tip={settingTips(roundLabel).assignmentMode}
      condNote={condNotes(roundLabel).assignmentMode}
      options={ASSIGNMENT_MODE_ORDER}
      copy={securityAssignmentModeCopy(roundLabel)}
      value={assignmentMode ?? RoundAssignmentMode.MATCH_STAGE}
      onChange={(next) => setValue('assignmentMode', next, { shouldDirty: true, shouldValidate: true })}
    />
  );
}

function GraceSetting({ label, help, intro }: { label: string; help: string; intro: string }) {
  const {
    control,
    formState: { errors },
  } = useFormContext<RoundCreationFormValues>();
  return (
    <Controller
      name='unassignedGraceDays'
      control={control}
      render={({ field }) => (
        <NumberSetting
          title={label}
          keyChip={CONFIG_KEYS.grace}
          tip={settingTips(getRoundLabel()).grace}
          help={`${intro} ${help}`}
          unit='days'
          value={field.value ?? null}
          onChange={(next) => field.onChange(next ?? '')}
          error={errors.unassignedGraceDays?.message}
          min={0}
          max={WINDOW_MAX_DAYS}
        />
      )}
    />
  );
}
